import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, CheckCircle } from "lucide-react";
import { SERVICE_DATA } from "@/data/services";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = SERVICE_DATA.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/" replace />;

  return (
    <div data-testid={`service-page-${slug}`}>
      {/* Hero */}
      <section className="relative pt-32 pb-24 sm:pt-44 sm:pb-36 md:pt-56 md:pb-44 overflow-hidden min-h-[600px]">
        <div className="absolute inset-0 bg-[#0f1419]">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" style={{objectPosition: 'center 83%', transform: service.imageScale ? `scale(${service.imageScale})` : undefined}} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1419]/95 via-[#0f1419]/60 to-[#0f1419]/10"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">
            Our Services
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white mb-6"
          >
            {service.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-base sm:text-lg text-white/60 max-w-2xl leading-relaxed"
          >
            {service.description}
          </motion.p>
        </div>
      </section>

      {/* Details */}
      <section className="py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-8">
                What We Offer
              </h2>
              <div className="space-y-5">
                {service.details.map((paragraph, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="text-white/50 text-sm sm:text-base leading-relaxed"
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              {/* Features Card */}
              <div className="glass-card rounded-2xl p-6 sm:p-8">
                <h3 className="font-heading text-lg font-bold text-white mb-6">Key Features</h3>
                <ul className="space-y-4">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                      <span className="text-white/70 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quick Contact Card */}
              <div className="glass-card rounded-2xl p-6 sm:p-8 text-center">
                <h3 className="font-heading text-lg font-bold text-white mb-3">Get a Free Quote</h3>
                <p className="text-white/50 text-sm mb-6">
                  {service.isCTAOnly
                    ? "City sign permits require a personalized consultation. Call us to discuss your project."
                    : `Interested in ${service.title.toLowerCase()}? Let's talk about your project.`
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  {service.isCTAOnly ? (
                    <a
                      href="tel:+17783220801"
                      data-testid="service-detail-call-btn"
                      className="flex-1 bg-gold text-black font-bold py-3 rounded-full text-sm text-center hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all flex items-center justify-center gap-2"
                    >
                      <Phone className="w-3.5 h-3.5" /> Call Us Now
                    </a>
                  ) : (
                    <>
                      <Link
                        to="/booking"
                        data-testid="service-detail-book-btn"
                        className="flex-1 bg-gold text-black font-bold py-3 rounded-full text-sm text-center hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all"
                      >
                        Book Consultation
                      </Link>
                      <a
                        href="tel:+17783220801"
                        data-testid="service-detail-call-btn"
                        className="flex-1 border border-white/20 text-white py-3 rounded-full text-sm text-center hover:bg-white/5 hover:border-gold/50 transition-all flex items-center justify-center gap-2"
                      >
                        <Phone className="w-3.5 h-3.5" /> Call Now
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA. same as homepage */}
      <section className="py-16 sm:py-28 md:py-40 relative overflow-hidden">
        <div className="absolute inset-0 bg-gold/5"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,191,0,0.4)] to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
              Ready to Make Your Business <span className="gold-shimmer">Shine?</span>
            </h2>
            <p className="text-white/50 text-base sm:text-lg mb-10 max-w-2xl mx-auto">
              Get a free consultation and discover how our custom signage solutions can transform your business visibility and attract more customers.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/booking"
                data-testid="service-cta-booking-btn"
                className="bg-gold text-black font-bold px-8 sm:px-10 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1 flex items-center gap-2 text-sm sm:text-base"
              >
                Book Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+17783220801"
                data-testid="service-cta-phone-btn"
                className="bg-transparent border border-white/20 text-white px-8 sm:px-10 py-4 rounded-full hover:bg-white/5 hover:border-gold/50 transition-all flex items-center gap-2 text-sm sm:text-base"
              >
                <Phone className="w-4 h-4" /> (778) 322-0801
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
